import React, { useState, useEffect } from 'react';
import '../styles/Faq.css'; // Import CSS styles
import flamegraphicon from '../assets/images/FlameGraphIcon.png';
import flamegraph from '../assets/images/FlameGraph.png';


const CONTACT_EMAIL = "prguardian@microsoft.com";

const copyToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text);
        alert('Email address copied to clipboard!');
    } catch (error) {
        console.error('Failed to copy text to clipboard:', error);
        alert('Failed to copy email address. This feature may not be supported in your current browser.');
    }
};

const FAQItem = ({ question, answer }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="faq-faq-item faq-sub-titles">
            <h4
                className="faq-question"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                tabIndex={0}
                onKeyPress={e => {
                    if (e.key === 'Enter' || e.key === ' ') setOpen(!open);
                }}
            >
                <span
                    className="faq-icon"
                    style={{
                        transform: open ? 'rotate(0deg)' : 'rotate(0deg)',
                    }}
                    aria-hidden="true"
                >
                    {open ? '−' : '+'}
                </span>
                {question}
            </h4>
            {open && <div className="faq-answer">{answer}</div>}
        </div>
    );
};

const FAQCategory = ({ title, icon, items }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="faq-category-box">
            <div
                className="faq-category-header"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                tabIndex={0}
                onKeyPress={e => {
                    if (e.key === 'Enter' || e.key === ' ') setOpen(!open);
                }}
            >
                <span
                    className="faq-icon"
                    style={{
                        transform: open ? 'rotate(0deg)' : 'rotate(0deg)',
                    }}
                    aria-hidden="true"
                >
                    {open ? '−' : '+'}
                </span>
                {icon && <span className="faq-category-icon">{icon}</span>}
                {title}
            </div>
            {open &&
                items.map((item, idx) => (
                    <FAQItem key={idx} question={item.question} answer={item.answer} />
                ))}
        </div>
    );
};

const FAQPage = () => {
    useEffect(() => {
        window.document.title = 'PR Guardian - FAQ';
    }, []);

    return (
        <div className="page-container">
            <div className="faq-contact-box">
                <span className="faq-contact-icon" aria-label="Contact">
                    &#9993;
                </span>
                <span className="faq-contact-text">
                    For questions or to onboard PR Guardian in your repository, please contact <b>{CONTACT_EMAIL}</b>
                </span>
                <button
                    className="faq-contact-button"
                    onClick={() => copyToClipboard(CONTACT_EMAIL)}
                    type="button"
                >
                    Copy Email
                </button>
            </div>
            <h2 className="faq-main-header">
                Frequently Asked Questions (FAQ)
            </h2>
            {faqCategories.map((cat, idx) => (
                <FAQCategory key={idx} title={cat.title} icon={cat.icon} items={cat.items} />
            ))}
        </div>
    );
};

const faqCategories = [
    {
        title: 'Overview',
        icon: '📖',
        items: [
            {
                question: 'What is PR Guardian?',
                answer: (
                    <>
                        <b>PR Guardian</b>  is a web application that offers an intuitive interface for tracking commits and identifying regressions across repositories.<br />
                        It monitors every PR and provides engineers with clear visibility into the impact of their changes through metrics, flame graphs, and Teams-integrated alerts. <br />
                    </>
                ),
            },
            {
                question: 'Goals and Capabilities?',
                answer: (
                    <>
                        <ul>
                            <li><b>Automatic Regression Detection:</b> PR Guardian proactively identifies problematic pull requests before they reach production.</li>
                            <li><b>Metric Monitoring:</b> Tracks key performance indicators such as CPU usage, waits, exceptions down to the method level.</li>
                            <li><b>Developer Feedback Loop:</b> Provides engineers with clear, actionable insights into the impact of their changes, including flame graphs and profiler traces.</li>
                        </ul>
                    </>
                )
            },
            {
                question: 'Is there any login required?',
                answer: (
                    <>

                        Yes, to access the application, you need to log in using your <span className="faq-underline">Microsoft credentials</span>. You can also sign out of your account using the signout button at the top right.
                    </>
                ),
            },
        ],
    },
    {
        title: 'How to Navigate',
        icon: '📝',
        items: [
            {
                question: 'What is displayed on the front-page?',
                answer: (
                    <>
                        After logging in, you'll land on the Commits List page. To switch services, use the dropdown menu on the top right.<br />
                        This page displays a paginated list of commits with details like commit ID, author, merge date, and more.
                    </>
                ),
            },
            {
                question: 'How do I navigate between pages of commits?',
                answer: (
                    <>
                        At the bottom of the commits list, you'll find "Previous" and "Next" buttons to navigate between pages. Click "Previous" to go back and "Next" to move forward.
                    </>
                ),
            },
            {
                question: 'What is displayed when I click on the PR?',
                answer: (
                    <>
                        The details for the selected commit are displayed. You can click the link in the top-right corner to open the corresponding PR in Azure DevOps. <br />
                        Use the dropdown menus to filter by <span className="faq-underline">environment, process, and metric</span>.
                        Setting the graph to <span className="faq-underline">Overall</span> displays the aggregated metric value, while selecting <span className="faq-underline">Function</span> shows individual graphs for each function.
                    </>
                ),
            },
            {
                question: 'How do I only see the list of commmits that were flagged as regressions?',
                answer: (
                    <>
                        Click <b>"Regression Only"</b> to filter and display only the commits that have been flagged as regressions.
                    </>
                ),
            },
        ],
    },
    {
        title: 'Search Functaionality',
        icon: '🔍',
        items: [
            {
                question: 'What does the search bar functionality provide?',
                answer: (
                    <>
                        You can use the dropdown search feature provided on the commits list page to search for commits by <b>4 different options</b>:
                        <ul>
                            <li>user alias (e.g. prguardian)</li>
                            <li>commitid (e.g. 32f5a20d76b6de0aebdca95)</li>
                            <li>build number (e.g. 1.0.01234.1234) - support wildcards</li>
                            <li>feature levers like killswitchid (e.g. 978C33F5-A589-4F87-A496) and flightid (e.g. Reserved_Server_ExpFeature00000)</li>
                        </ul>
                    </>
                ),
            },
            {
                question: 'How do I filter commits by date?',
                answer: (
                    <>
                        You can filter commits by event date (e.g. deployment date, flight activation date) and by farm name. <br />
                        Enter the start and end dates and click the "Update" button to apply the filter.
                    </>
                ),
            },
        ],
    },
    {
        title: 'Flame Graphs',
        icon: '📊',
        items: [
            {
                question: 'What does flame graph show?',
                answer: (
                    <>
                        A flame graph is a visualization tool used to identify performance bottlenecks in code. It displays <b>stack traces</b> as layered bars: <br />
                        <ul>
                            <li>Width of each bar shows how often a function appears in samples</li>
                            <li>Height represents the depth of the call stack</li>
                        </ul>
                        You can click the frame to see the percentage of metric it is accounting for.
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                            <img src={flamegraph} alt="Flame Graph" style={{ maxWidth: 500, width: '100%', height: 'auto' }} />
                        </div>
                    </>
                ),
            },
            {
                question: 'How do I view flame graphs for a PR?',
                answer: (
                    <>
                        You can hover over a data point on the graph to reveal the graph icon. Click the icon to open the corresponding flame graph. <br />
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                            <img src={flamegraphicon} alt="Flame Graph Icon" style={{ maxWidth: 200, width: '100%', height: 'auto' }} />
                        </div>
                    </>
                )
            },
        ],
    },
    {
        title: 'Components',
        icon: '📓',
        items: [
            {
                question: 'What does component tab show?',
                answer: (
                    <>
                        The Components tab displays a list of all tracked components, including their<span className="faq-underline">namespaces, classes, and methods</span>. <br />
                        It provides an overview of the <b>system-level trace data</b>, categorized into high-level modules, helping you understand where your feature is consuming resources.
                    </>
                ),
            },
            {
                question: 'How can I interpret the graphs?',
                answer: (
                    <>
                        The graphs provide visual insights into your component's performance metrics. You can: <br />
                        <ul>
                            <li>Filter by environment, metric, and date range</li>
                            <li>Analyze trends over time</li>
                            <li>Spot anomalies or regressions before they impact production</li>
                        </ul>
                    </>
                )
            },
            {
                question: 'What should I do if I notice a performance issue?',
                answer: (
                    <>
                        <ul>
                            <li>Use the <b>detailed component view</b> to identify which namespace, class, or method is responsible</li>
                            <li>Investigate the root cause using trace data</li>
                            <li>Take corrective action early to prevent downstream impact</li>
                        </ul>
                    </>
                )
            }
        ],
    },
];

export default FAQPage;